<template>
  <div class="flex flex-col gap-y-4">
    <ul class="flex flex-col gap-y-2">
      <li class="flex items-start gap-1">
        <img alt="" class="ml-0.5 mt-0.5 h-4 w-4 shrink-0" src="@/assets/icon/calendar_today/gray.svg" />
        <AtomsParagraph2 class="text-[17px]">
          {{ firstPreferredDateTime }}
        </AtomsParagraph2>
      </li>
      <li class="flex items-start gap-1">
        <img alt="" class="mt-0.5 h-5 w-5 shrink-0" src="@/assets/icon/group.svg" />
        <AtomsParagraph2 class="text-[17px]">
          {{ participantsLabel }}
        </AtomsParagraph2>
      </li>
      <li class="ml-0.5 flex items-start gap-1.5">
        <img alt="" class="mt-0.5 h-4 w-4 shrink-0" src="@/assets/icon/event_available/gray.svg" />
        <AtomsParagraph2 class="text-[17px]">
          {{ cancelPolicyLabel }}
        </AtomsParagraph2>
      </li>
    </ul>
    <AtomsBorder1 />
    <template v-if="shouldShowPrice">
      <div class="mb-4 flex flex-col">
        <div class="mb-0.5 flex items-end justify-between">
          <AtomsParagraph2 class="text-base md:text-[17px]">Total (JPY)</AtomsParagraph2>
          <p class="text-[28px] leading-8 text-black2 md:text-[30px] md:leading-10">¥{{ totalPriceString }}</p>
        </div>
        <div class="flex items-start justify-between">
          <button class="flex w-max items-center gap-1 text-gold1 underline" type="button" @click="isDetailsOpen = !isDetailsOpen">
            <span>Details</span>
            <img v-if="!isDetailsOpen" alt="" class="h-2.5 w-2.5" src="@/assets/icon/down/default.svg" />
            <img v-else alt="" class="h-2.5 w-2.5" src="@/assets/icon/up/default.svg" />
          </button>
          <p class="-mt-1.5 text-[13px] text-gray3">tax incl.</p>
        </div>
        <div v-if="isDetailsOpen">
          <TotalPriceSection
            v-if="!props.experienceEvent.timeSlotId && props.preReservationPrice"
            :applied-code="reservationFormStep2.code"
            :number-of-participants="reservationFormStep1.numOfParticipants"
            :pre-reservation-price="props.preReservationPrice"
            :selected-experience-event="props.experienceEvent"
            :selected-experience-event-options="reservationFormStep2.experienceEventOptions"
            :selected-interpreter-option="reservationFormStep2.experienceEventInterpreterOption"
          />
          <TotalPriceSectionWithGuestType
            v-else-if="props.preReservationPriceWithGuestType"
            :applied-code="reservationFormStep2.code"
            :experience-event="props.experienceEvent"
            :pre-reservation-price-with-guest-type="props.preReservationPriceWithGuestType"
            :readonly="true"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import TotalPriceSection from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TotalPriceSection.vue';
import TotalPriceSectionWithGuestType from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TotalPriceSectionWithGuestType.vue';
import { type ExperiencePreReservationPriceViewModel, type WabunkaReservationFormStep1, type WabunkaReservationFormStep2 } from '@/wabunka-src/features/Experience';
import { cancelPolicyText } from '@/wabunka-src/features/Experience/models/cancellationPolicyTrigger';
import { buildWabunkaExperienceEventPreferredDateTime, type WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import { formatGuestCountsLabel } from '@/wabunka-src/features/Experience/models/experienceEventFee';
import type { ExperiencePreReservationPriceWithGuestTypeViewModel } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';

interface Props {
  reservationFormStep1: WabunkaReservationFormStep1;
  reservationFormStep2: WabunkaReservationFormStep2;
  experienceEvent: WabunkaExperienceEventViewModel;
  preReservationPrice?: ExperiencePreReservationPriceViewModel;
  preReservationPriceWithGuestType?: ExperiencePreReservationPriceWithGuestTypeViewModel;
}

const props = defineProps<Props>();

const shouldShowPrice = computed(() => props.experienceEvent.experience.isPublicPrice);

const isDetailsOpen = ref(false);

const totalPriceString = computed(() => {
  if (!props.experienceEvent.timeSlotId && props.preReservationPrice) {
    return props.preReservationPrice.totalPriceIncludingTaxString;
  }
  return props.preReservationPriceWithGuestType?.totalPriceIncludingTaxString ?? '0';
});

const firstPreferredDateTime = computed(() => buildWabunkaExperienceEventPreferredDateTime(props.experienceEvent));

const participantsLabel = computed(() => {
  if (props.experienceEvent.timeSlotId && props.reservationFormStep1.guestCounts && props.experienceEvent.experienceEventFees?.length) {
    const label = formatGuestCountsLabel(props.reservationFormStep1.guestCounts, props.experienceEvent.experienceEventFees);
    if (label) return label;
  }
  const n = props.reservationFormStep1.guestCounts != null ? Object.values(props.reservationFormStep1.guestCounts).reduce((sum, c) => sum + c, 0) : props.reservationFormStep1.numOfParticipants;
  return `${n} participant${n !== 1 ? 's' : ''}`;
});

const cancelPolicyLabel = computed(() => {
  return cancelPolicyText(props.experienceEvent.cancellationPolicyTrigger, props.experienceEvent.experience.refundableCancellationDaysForGuest);
});
</script>
